import { createContext, useContext } from "react";

interface LayoutPreferenceContextProps {
  // biome-ignore lint: no-any
  layoutPreferences: any;
}

export const LayoutPreferenceContext = createContext<
  LayoutPreferenceContextProps | undefined
>(undefined);

export const useLayoutPreferences = () => {
  const context = useContext(LayoutPreferenceContext);

  const hasLayoutPreference = (key: string): boolean => {
    if (!context) {
      throw new Error(
        "useLayoutPreference must be used within a LayoutPreferenceProvider",
      );
    }
    return context ? context.layoutPreferences[key] !== undefined : false;
  };

  const getLayoutPreference = (key: string): unknown => {
    if (!context) {
      throw new Error(
        "useLayoutPreference must be used within a LayoutPreferenceProvider",
      );
    }
    return context ? context.layoutPreferences[key] : undefined;
  };

  const setLayoutPreference = (key: string, value: unknown) => {
    if (!context) {
      throw new Error(
        "useLayoutPreference must be used within a LayoutPreferenceProvider",
      );
    }
    const layoutPreferences: { [key: string]: unknown } =
      context.layoutPreferences || {};
    layoutPreferences[key] = value;
    document.cookie = `user-preferences=${JSON.stringify(
      layoutPreferences,
    )}; path=/`;
  };

  const clearLayoutPreferences = (key: string) => {
    if (!context) {
      throw new Error(
        "useLayoutPreference must be used within a LayoutPreferenceProvider",
      );
    }
    const layoutPreferences: { [key: string]: unknown } =
      context.layoutPreferences || {};
    delete layoutPreferences[key];
    document.cookie = `user-preferences=${JSON.stringify(
      layoutPreferences,
    )}; path=/`;
  };

  const getBoolPref = (key: string, defaultValue: boolean): boolean => {
    const pref = getLayoutPreference(key);
    if (pref === undefined) {
      return defaultValue;
    }
    return pref === "1";
  };

  const setBoolPref = (key: string, value: boolean) => {
    setLayoutPreference(key, value ? "1" : "0");
  };

  return {
    getBoolPref,
    setBoolPref,
    getLayoutPreference,
    setLayoutPreference,
    clearLayoutPreferences,
    hasLayoutPreference,
  };
};
